<template>
  <v-container>
    <!-- Título e Botões -->
    <v-row class="align-center mb-5">
      <v-col cols="auto">
        <h3 class="page-title">
          <v-icon color="primary" class="mr-2">mdi-target</v-icon>
          Metas
        </h3>
      </v-col>

      <v-spacer></v-spacer>

      <v-col cols="auto">
        <!-- Botão para reabrir o tour desta aba -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              color="primary"
              v-bind="attrs"
              v-on="on"
              aria-label="Ver o tour desta aba"
              class="mr-3"
              @click="startTour"
            >
              <v-icon>mdi-help-circle-outline</v-icon>
            </v-btn>
          </template>
          <span>Ver o tour desta aba</span>
        </v-tooltip>

        <!-- Botão para atualizar a lista -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              color="primary"
              v-bind="attrs"
              v-on="on"
              :loading="isLoading"
              class="mr-3"
              @click="loadGoals"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </template>
          <span>Atualizar lista</span>
        </v-tooltip>

        <!-- Botão para adicionar meta -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              color="primary"
              v-bind="attrs"
              v-on="on"
              @click="openAddGoalDialog"
              class="mr-3"
              data-tour="gl-add"
            >
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </template>
          <span>Adicionar meta</span>
        </v-tooltip>

        <!-- Botão para buscar -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              color="primary"
              v-bind="attrs"
              v-on="on"
              @click="searchGoals"
              class="mr-3"
            >
              <v-icon>mdi-magnify</v-icon>
            </v-btn>
          </template>
          <span>Buscar metas</span>
        </v-tooltip>

        <!-- Botão de ordenação -->
        <v-menu v-model="menuOpen" :close-on-content-click="false" offset-y>
          <template v-slot:activator="{ on: menu, attrs }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tooltip }">
                <v-btn icon color="primary" v-bind="attrs" v-on="{ ...menu, ...tooltip }">
                  <v-icon>mdi-sort</v-icon>
                </v-btn>
              </template>
              <span>Ordenar metas</span>
            </v-tooltip>
          </template>

          <v-list>
            <v-list-item @click="sortGoals('asc')">
              <v-list-item-title>Crescente</v-list-item-title>
            </v-list-item>
            <v-list-item @click="sortGoals('desc')">
              <v-list-item-title>Decrescente</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-col>
    </v-row>

    <!-- Tabela -->
    <v-data-table
      :headers="headers"
      :items="goals"
      item-key="id"
      class="elevation-1"
      data-tour="gl-list"
      :loading="isLoading"
      loading-text="Carregando..."
      :no-data-text="'Nenhum registro encontrado'"
      :footer-props="{
        itemsPerPageText: 'Itens por página:',
        pageText: '{0}-{1} de {2}',
      }"
    >
      <template v-slot:item="{ item, index }">
        <tr>
          <td>{{ item.goal_name }}</td>
          <td>{{ item.goal_type }}</td>
          <td>
            <template
              v-if="item.current_value != null || item.target_value != null"
            >
              {{ item.current_value != null ? item.current_value : "—" }}
              {{ item.unit || "" }}
              /
              {{ item.target_value != null ? item.target_value : "—" }}
              {{ item.unit || "" }}
            </template>
            <span v-else class="empty-value">Não definido</span>
          </td>
          <td>{{ formatDate(item.start_date) }}</td>
          <td>{{ formatDate(item.target_date) }}</td>
          <td>
            <!-- Indicador de status -->
            <v-chip
              :color="getStatusColor(item.status)"
              small
              text-color="white"
            >
              {{ item.status }}
            </v-chip>
          </td>

          <td class="text-center actions-cell">
            <!-- Botões de ação -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  @click="editGoal(item)"
                  class="mr-1"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>Editar meta</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="error"
                  v-bind="attrs"
                  v-on="on"
                  @click="deleteGoal(item)"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>Excluir meta</span>
            </v-tooltip>
          </td>

          <!-- Checkbox -->
          <td
            class="text-center situation-cell"
            :data-tour="index === 0 ? 'gl-status' : null"
          >
            <!-- Checkbox: Cancelado -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on" class="d-inline-block">
                  <v-checkbox
                    v-model="item.isCanceled"
                    @change="onCanceledChange(item)"
                    :disabled="item.status === 'Concluído'"
                    hide-details
                    color="error"
                    label="✖"
                    class="ma-0 d-inline-flex"
                  />
                </div>
              </template>
              <span>{{
                item.status === "Concluído"
                  ? "Meta já concluída"
                  : "Marcar como cancelada"
              }}</span>
            </v-tooltip>

            <!-- Checkbox: Concluído -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on" class="d-inline-block">
                  <v-checkbox
                    v-model="item.isCompleted"
                    @change="onCompletedChange(item)"
                    :disabled="item.status === 'Cancelado'"
                    hide-details
                    color="success"
                    label="✔"
                    class="ma-0 ml-2 d-inline-flex"
                  />
                </div>
              </template>
              <span>{{
                item.status === "Cancelado"
                  ? "Meta já cancelada"
                  : "Marcar como concluída"
              }}</span>
            </v-tooltip>
          </td>
        </tr>
      </template>
    </v-data-table>

    <GoalFormDialog
      v-model="goalDialog"
      :patient-id="$route.params.id"
      :goal="editingGoal"
      @saved="loadGoals"
    />

  </v-container>
</template>

<script>
import http from "@/services/http";
import { useSnackbar } from "@/composables/useSnackbar";
import GoalFormDialog from "@/features/patients/components/GoalFormDialog.vue";
import tourMixin from "@/features/tour/tourMixin";
import { formatDate } from "@/utils/datetime";

export default {
  mixins: [tourMixin],

  components: { GoalFormDialog },

  setup() {
    const { success, error, warning, info, erroDeRequisicao } = useSnackbar();
    return { success, error, warning, info, erroDeRequisicao };
  },

  data() {
    return {
      goalDialog: false,
      editingGoal: null,
      menuOpen: false,
      isDeleting: false,
      isLoading: false,
      goalToDelete: null,

      headers: [
        { text: "Meta",      value: "goal_name" },
        { text: "Tipo",      value: "goal_type" },
        { text: "Progresso", value: "progress" },
        { text: "Início",    value: "start_date" },
        { text: "Alvo",      value: "target_date" },
        { text: "Status",    value: "status" },
        { text: "Ações",     value: "actions", sortable: false, align: "center" },
        { text: "Situação",  sortable: false, align: "center" },
      ],

      goals: [],
    };
  },

  created() {
    this.loadGoals();
  },

  computed: {
    tourId() {
      return "sec-metas-pg";
    },
  },

  methods: {
    formatDate,
    tourReady() {
      return !this.isLoading;
    },

    tourSteps() {
      return [
        {
          title: "Metas do paciente",
          description:
            "Aqui você define e acompanha as metas do paciente ao longo do tratamento. Vou te mostrar como funciona.",
        },
        {
          anchor: "gl-add",
          title: "Adicionar meta",
          description:
            "Crie uma nova meta informando nome, tipo, valores de partida e alvo, e as datas de início e conclusão.",
        },
        {
          anchor: "gl-list",
          title: "Suas metas",
          description:
            "Cada linha mostra a meta, o progresso, os prazos e o status atual. Nas ações você edita ou remove.",
        },
        {
          anchor: "gl-status",
          title: "Situação da meta",
          description:
            "Use as marcações para dar a meta como concluída ou cancelada — o status é atualizado na hora.",
        },
      ];
    },

    async loadGoals() {
      const patientId = this.$route.params.id;
      this.isLoading = true;
      try {
        const response = await http.get(`/api/patient-goals/${patientId}`);

        this.goals = (response.data.data || []).map((goal) => ({
          ...goal,
          isCompleted: goal.status === "Concluído",
          isCanceled: goal.status === "Cancelado",
        }));

        if (this.goals.length === 0) {
          this.warning("Sem registro de metas para este paciente!");
        } else {
          this.success("Metas carregadas com sucesso!");
        }
      } catch (error) {
        console.error("Erro ao carregar metas:", error);
        this.erroDeRequisicao(error, "Erro ao carregar metas. Tente novamente.");
      } finally {
        this.isLoading = false;
      }
    },

    openAddGoalDialog() {
      this.editingGoal = null;
      this.goalDialog = true;
    },

    editGoal(item) {
      this.editingGoal = { ...item };
      this.goalDialog = true;
    },

    async deleteGoal(item) {
      this.goalToDelete = item;
      const ok = await this.$confirm({
        type: "delete",
        title: "Confirmar exclusão",
        message: "Você tem certeza que deseja excluir esta meta?",
        hint: "Esta ação não pode ser desfeita.",
      });
      if (!ok) {
        this.goalToDelete = null;
        return;
      }
      await this.confirmDelete();
    },

    async confirmDelete() {
      if (!this.goalToDelete) return;

      this.isDeleting = true;

      try {
        await http.delete(`/api/patient-goals/delete/${this.goalToDelete.id}`);
        this.success("Meta excluída com sucesso!");

        this.goalToDelete = null;
        this.loadGoals();
      } catch (error) {
        console.error("Erro ao excluir meta:", error);
        this.erroDeRequisicao(error, "Erro ao excluir meta");
      } finally {
        this.isDeleting = false;
      }
    },

    searchGoals() {
      this.info("Funcionalidade de busca a ser implementada");
    },

    sortGoals(direction) {
      this.goals.sort((a, b) => {
        if (direction === "asc") {
          return new Date(a.target_date) - new Date(b.target_date);
        } else {
          return new Date(b.target_date) - new Date(a.target_date);
        }
      });
      this.menuOpen = false;
    },

    getStatusColor(status) {
      const colors = {
        "Não Iniciado": "blue-grey",
        "Em Andamento": "blue",
        Concluído: "green",
        Cancelado: "red",
      };
      return colors[status] || "blue-grey";
    },

    async onCompletedChange(item) {
      const newStatus = item.isCompleted ? "Concluído" : "Em Andamento";
      if (item.status === "Cancelado") return;

      try {
        const response = await http.put(
          `/api/patient-goals/update-status/${item.id}`,
          { status: newStatus },
        );

        item.status = response.data.data.status;
        item.isCanceled = false;
        this.success(`Status alterado para ${newStatus}`);
      } catch (err) {
        this.erroDeRequisicao(err, "Erro ao atualizar status");
      }
    },

    async onCanceledChange(item) {
      const newStatus = item.isCanceled ? "Cancelado" : "Em Andamento";
      if (item.status === "Concluído") return;

      try {
        const response = await http.put(
          `/api/patient-goals/update-status/${item.id}`,
          { status: newStatus },
        );

        item.status = response.data.data.status;
        item.isCompleted = false;
        this.success(`Status alterado para ${newStatus}`);
      } catch (err) {
        this.erroDeRequisicao(err, "Erro ao atualizar status");
      }
    },
  },
};
</script>

<style scoped>
/* ═══ Page title ═══ */
.page-title {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: 700;
  color: var(--ds-text-strong);
  letter-spacing: -0.3px;
  margin: 0;
}

/* ═══ Section label ═══ */
.ds-section-label {
  display: flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  color: var(--ds-primary);
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

/* ═══ Tabela padronizada (estilo BodyCompositionDialog) ═══ */
.v-data-table >>> thead th {
  background-color: var(--ds-blue-50) !important;
  border-bottom: 2px solid var(--ds-blue-100) !important;
  padding: 12px !important;
  font-size: 10px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.4px !important;
  color: var(--ds-primary) !important;
  font-weight: 700 !important;
  height: auto !important;
}

.v-data-table >>> tbody tr {
  transition: background-color 0.15s ease;
}

.v-data-table >>> tbody tr:hover {
  background-color: var(--ds-blue-50) !important;
}

.v-data-table >>> tbody td {
  border-bottom: 1px solid var(--ds-blue-divider) !important;
  font-size: 12px !important;
  color: var(--ds-text-600) !important;
}

.progress-column {
  min-width: 150px;
}

/* ═══ Padronização de células ═══ */
.actions-cell {
  white-space: nowrap;
  min-width: 96px;
}

.situation-cell {
  white-space: nowrap;
  min-width: 120px;
}

.empty-value {
  color: #A9D6C5;
  font-style: italic;
  font-size: 12px;
}

.v-card__title.primary {
  border-radius: 4px 4px 0 0;
}

.text-h6 {
  font-weight: 600;
}

/* ═══ Design system dialog ═══ */
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
}
.ds-dialog-header--danger {
  background: linear-gradient(135deg, var(--ds-danger) 0%, var(--ds-danger-mid) 60%, var(--ds-danger-light) 100%) !important;
}
.close-btn {
  position: relative;
  z-index: 1;
}
.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.15) !important;
}
</style>
